"use client";

import { useState, useTransition } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { TRADE_CATEGORIES, getTradeName } from "@/lib/trades";
import { useBlockedMemberIds } from "@/app/components/UserSafety";

interface Question {
  user_id: string | null;
  id: string;
  title: string;
  slug: string;
  trade_category: string | null;
  status: string;
  view_count: number;
  created_at: string;
  answer_count: number;
  author_name: string;
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export function AskClient({
  initialQuestions,
  initialTrade,
  initialStatus,
  initialSearch,
}: {
  initialQuestions: Question[];
  initialTrade: string;
  initialStatus: string;
  initialSearch: string;
}) {
  const router = useRouter();
  const blockedIds = useBlockedMemberIds();
  const [isPending, startTransition] = useTransition();
  const [search, setSearch] = useState(initialSearch);
  const [trade, setTrade] = useState(initialTrade);
  const [status, setStatus] = useState(initialStatus);

  function applyFilters(newTrade?: string, newStatus?: string) {
    const t = newTrade ?? trade;
    const s = newStatus ?? status;
    const params = new URLSearchParams();
    if (t) params.set("trade", t);
    if (s) params.set("status", s);
    if (search) params.set("q", search);
    startTransition(() => {
      router.push(`/ask${params.toString() ? `?${params}` : ""}`);
    });
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    applyFilters();
  }

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 pt-28 pb-16">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-[28px] md:text-[38px] font-bold text-charcoal leading-tight">
            Ask a Tradesperson
          </h1>
          <p className="text-text-muted mt-2 text-[15px]">
            Get free advice from tradespeople
          </p>
        </div>
        <Link
          href="/ask/new"
          className="shrink-0 bg-forest text-white font-semibold text-sm px-5 py-2.5 rounded-sm no-underline hover:bg-forest-light transition-colors"
        >
          Ask a Question
        </Link>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative">
          <svg
            className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search questions..."
            className="w-full pl-10 pr-4 py-3 bg-white border border-border-light rounded-sm text-sm text-text-body placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
          />
        </div>
      </form>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-8">
        <select
          value={trade}
          onChange={(e) => {
            setTrade(e.target.value);
            applyFilters(e.target.value, undefined);
          }}
          className="bg-white border border-border-light rounded-sm px-3 py-2 text-sm text-text-body focus:outline-none focus:ring-2 focus:ring-forest/20"
        >
          <option value="">All trades</option>
          {TRADE_CATEGORIES.map((t) => (
            <option key={t.slug} value={t.slug}>
              {t.name}
            </option>
          ))}
        </select>

        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            applyFilters(undefined, e.target.value);
          }}
          className="bg-white border border-border-light rounded-sm px-3 py-2 text-sm text-text-body focus:outline-none focus:ring-2 focus:ring-forest/20"
        >
          <option value="">All questions</option>
          <option value="open">Open</option>
          <option value="answered">Answered</option>
        </select>
      </div>

      {/* Question list */}
      <div className={`space-y-3 ${isPending ? "opacity-60" : ""}`}>
        {initialQuestions.length === 0 && (
          <div className="text-center py-16">
            <p className="text-text-muted text-[15px] mb-4">
              {search || trade || status
                ? "No questions match your filters."
                : "No questions yet. Be the first to ask!"}
            </p>
            <Link
              href="/ask/new"
              className="inline-block bg-forest text-white font-semibold text-sm px-5 py-2.5 rounded-sm no-underline hover:bg-forest-light transition-colors"
            >
              Ask a Question
            </Link>
          </div>
        )}

        {initialQuestions.filter(q => !q.user_id || !blockedIds.includes(q.user_id)).map((q) => (
          <Link
            key={q.id}
            href={`/ask/${q.slug}`}
            className="block bg-white border border-border-light rounded-md p-5 hover:shadow-md transition-shadow no-underline group"
          >
            <h2 className="font-semibold text-[15px] text-text-body group-hover:text-forest transition-colors leading-snug mb-2">
              {q.title}
            </h2>
            <div className="flex flex-wrap items-center gap-3 text-xs text-text-muted">
              {q.trade_category && (
                <span className="inline-flex items-center px-2 py-0.5 bg-sage-pale text-forest rounded-sm font-medium">
                  {getTradeName(q.trade_category)}
                </span>
              )}
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {q.answer_count} {q.answer_count === 1 ? "answer" : "answers"}
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {q.view_count}
              </span>
              <span>{q.author_name}</span>
              <span>{timeAgo(q.created_at)}</span>
              {q.status === "open" && (
                <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-sm font-medium">
                  Open
                </span>
              )}
              {q.status === "answered" && (
                <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-sm font-medium">
                  Answered
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
